import type {
  AnnotatedPdfShape,
  AnnotatedShape,
  Annotation,
  ImageShape,
  VideoShape,
} from './annotation';
import type { DraftShape } from './comments.store';

type AssetKind = 'image' | 'video' | 'pdf';

export interface BuildAnnotationPayloadOptions {
  isEditing: boolean;
  assetKind: AssetKind;
  /** Timestamp captured at tool activation (video edit-mode also primes this). */
  capturedTimestamp: number | null;
  /** Live playhead reader — used as the video timestamp fallback when `capturedTimestamp` is null. */
  getCurrentTime: () => number | null;
}

/**
 * Builds the persisted annotation payload from the current draft.
 *
 * - `undefined` → no draft (a brand-new comment without annotations).
 * - `null`      → edit mode + the user explicitly cleared the existing
 *                 annotation (caller persists this as a clear).
 * - otherwise   → freshly-built payload (`{ kind: 'image' | 'video' | 'pdf' }`).
 *
 * Video always emits a `VideoAnnotation` on new comments so a timestamp-only
 * comment still pins to a frame; PDF carries each shape's own page so a
 * single comment can span pages.
 */
export function buildAnnotationPayload(
  draftShapes: DraftShape[],
  { isEditing, assetKind, capturedTimestamp, getCurrentTime }: BuildAnnotationPayloadOptions,
): Annotation | null | undefined {
  if (assetKind === 'pdf') {
    const pdfShapes: AnnotatedPdfShape[] = [];
    for (const d of draftShapes) {
      if (typeof d.page !== 'number') continue;
      pdfShapes.push({ shape: d.shape, color: d.color, page: d.page });
    }
    if (pdfShapes.length === 0) return isEditing ? null : undefined;
    return { kind: 'pdf', shapes: pdfShapes };
  }

  if (assetKind === 'video') {
    const filtered: AnnotatedShape<VideoShape>[] = [];
    for (const d of draftShapes) {
      if (d.shape.type === 'text-highlight') continue;
      filtered.push({ shape: d.shape, color: d.color });
    }
    const timestamp = capturedTimestamp ?? getCurrentTime() ?? 0;
    if (filtered.length > 0) return { kind: 'video', timestamp, shapes: filtered };
    return isEditing ? null : { kind: 'video', timestamp, shapes: [] };
  }

  // image (default)
  const imageShapes: AnnotatedShape<ImageShape>[] = [];
  for (const d of draftShapes) {
    if (d.shape.type === 'text-highlight') continue;
    imageShapes.push({ shape: d.shape, color: d.color });
  }
  if (imageShapes.length > 0) return { kind: 'image', shapes: imageShapes };
  return isEditing ? null : undefined;
}
